<template>
  <div>
    <v-divider />

    <v-row
      dense
      no-gutters
    >
      <v-col
        @click="viewSelectedResults = false"
        @keydown.enter="viewSelectedResults = false"
        :class="!viewSelectedResults ? 'elevation-2' : 'elevation-0'"
        class="pointer"
      >
        <div
          :class="!viewSelectedResults ? 'bg-primary c-white' : 'bg-white c-black'"
          class="fw-500 py-4 d-flex align-center justify-center"
          data-cy="search-mode-button"
          tabindex="0"
        >
          {{ $bct('Search') }}
        </div>
      </v-col>
      <v-col
        @click="viewSelectedResults = true"
        @keydown.enter="viewSelectedResults = true"
        :class="viewSelectedResults ? 'elevation-2' : 'elevation-0'"
        class="pointer"
        cols="6"
        data-testid="viewSelectedResultsBtn"
      >
        <div
          :class="viewSelectedResults ? 'bg-primary c-white' : 'bg-white c-black'"
          class="fw-500 py-4 d-flex align-center justify-center"
          data-cy="selected-mode-button"
          tabindex="0"
        >
          {{ $bct('Selected') }} ({{ programIds.length }})
        </div>
      </v-col>
    </v-row>

    <v-divider />

    <div class="bg-super-light-blue px-4">
      <div
        v-show="viewSelectedResults"
        class="py-3"
      >
        <div
          v-for="(programId, index) in programIds"
          :key="programId"
          class="mb-6"
        >
          <SelectedProgramCard
            @move="$emit('move', [index, $event])"
            @open="$emit('open', $event)"
            @remove="$emit('remove', programId)"
            :data-testid="'selectedResult' + index"
            :index="index"
            :length="programIds.length"
            :program-id="programId"
            class="my-4"
            active
            ordered
          />
        </div>
      </div>

      <div
        v-show="!viewSelectedResults"
        class="py-2"
      >
        <v-tabs
          v-model="searchTab"
          @update:model-value="onSearchTabChange"
          :disabled="localProcessing"
          bg-color="transparent"
          class="mb-2"
        >
          <v-tab
            class="ls-normal tt-none fs-16 fw-500"
            data-cy="address-mode-button"
          >
            {{ $bct('Search by address') }}
          </v-tab>
          <v-tab
            class="ls-normal tt-none fs-16 fw-500"
            data-cy="name-mode-button"
            data-testid="name-mode-button"
          >
            {{ $bct('Search by name') }}
          </v-tab>
        </v-tabs>

        <v-card
          flat
          tile
        >
          <v-row
            class="flex-column flex-sm-row"
            no-gutters
          >
            <v-col class="d-flex flex-grow-1 flex-shrink-0">
              <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledTextfield -->
              <v-text-field
                v-model="searchQuery"
                @keydown.enter="searchProviders"
                @update:model-value="searchQueryChanged = true"
                id="search_input"
                ref="search"
                :aria-label="searchTab == 0 ? $bct('Search by address') : $bct('Search by name')"
                class="b-radius-0 elevation-0"
                data-cy="query-input"
                data-testid="query-input"
                prepend-inner-icon="search"
                variant="solo"
                flat
                hide-details
                tile
              />
            </v-col>
            <v-col class="d-flex justify-end flex-grow-0 flex-shrink-0">
              <v-btn
                @click="searchProviders"
                :disabled="!searchQueryChanged"
                :height="58"
                :loading="searching"
                :ripple="false"
                class="focus-very-visible w-100 w-md-auto"
                color="primary"
                data-cy="search-button"
                data-testid="search-button"
                size="x-large"
                tabindex="0"
                tile
              >
                {{ $bct('Search') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <v-row
          v-if="subsidyProgram.selection_minimum || subsidyProgram.selection_limit"
          class="mt-0"
        >
          <v-col>
            <span
              class="fs-16 fw-800 c-primary"
              data-testid="selection-text"
            >
              <template v-if="subsidyProgram.selection_limit && !subsidyProgram.selection_minimum">
                {{ $bct(`You can select ${subsidyProgram.selection_limit} total options`) }}
              </template>
              <template
                v-else-if="!subsidyProgram.selection_limit && subsidyProgram.selection_minimum"
              >
                {{ $bct(`You must select ${subsidyProgram.selection_minimum} options`) }}
              </template>
              <template v-else>
                {{
                  $bct(
                    `You must select between ${subsidyProgram.selection_minimum} and ${subsidyProgram.selection_limit} options`,
                  )
                }}
              </template>
            </span>
          </v-col>
        </v-row>

        <v-btn
          v-if="!programIds.length > 0 && previousSubsidyProgramIds"
          @click="copyPrevious"
          class="mt-2"
          color="primary"
          prepend-icon="redo"
        >
          {{ $bct('Copy selections from') }}: {{ previousChildName }}
        </v-btn>

        <p
          v-if="matches.length == 0 && searchCounter == 0"
          class="fs-16 mt-2"
          data-cy="results-hint"
        >
          <span class="me-1">
            {{ $bct('Search by your address to see results near you.') }}
          </span>
        </p>

        <template v-if="searched && searchTab == 0 && searchCounter > 0">
          <v-row
            class="d-flex align-center mt-2"
            data-testid="search-filters"
          >
            <v-col
              cols="12"
              sm="8"
            >
              <v-btn
                v-if="subsidyProgram.enable_search_dob_eligibility_filter"
                :class="checkboxFilterButtonClass"
                class="focus-visible"
                elevation="0"
                variant="outlined"
                rounded
              >
                <v-checkbox
                  v-model="excludeIneligibleByDobChecked"
                  @update:model-value="toggleSearchDobEligibilityFilter"
                  :label="$bct('Show eligible options only')"
                  density="compact"
                  outlined
                  rounded
                />
              </v-btn>
              <v-btn
                v-if="subsidyProgram.enable_show_seat_availability"
                :class="checkboxFilterButtonClass"
                class="focus-visible"
                elevation="0"
                variant="outlined"
                rounded
              >
                <v-checkbox
                  v-model="filters.require_seat_availability"
                  @update:model-value="searchProviders"
                  :label="$bct('Show programs with available seats only')"
                  density="compact"
                  outlined
                  rounded
                />
              </v-btn>

              <template
                v-if="
                  store.state.schemas[subsidyProgram.provider_schema_id]?.meta?.openings?.enabled &&
                  store.state.schemas[subsidyProgram.provider_schema_id]?.meta?.openings?.public
                "
              >
                <FilterMenu
                  @clear="filters.vacancy = null"
                  @save="save"
                  :actions="true"
                  :active="filters.vacancy"
                  :active-classes="['bg-white', 'bc-primary', 'b-1']"
                  :inactive-classes="['bg-white', 'bc-very-light-grey']"
                  classes="bc-outlined-gray"
                  data-cy="vacancy-filter"
                  tid="search_availability_filter"
                  title="Availability"
                  hide-clear-filter
                >
                  <template #card>
                    <v-checkbox
                      v-model="filters.vacancy"
                      class="mt-0 mb-0"
                      color="primary"
                      false-icon="check_box_outline_blank"
                      true-icon="check_box"
                      hide-details
                    >
                      <template #label>
                        <span
                          class="c-black"
                          data-cy="availability-checkmark"
                        >
                          {{ $bct('Only show providers that have reported availability') }}
                        </span>
                      </template>
                    </v-checkbox>
                  </template>
                </FilterMenu>
              </template>

              <v-btn
                v-if="searchFlags.moreFiltersEnabled"
                @click="moreFiltersDialogIsVisible = true"
                :class="moreFiltersButtonClass"
                class="focus-visible"
                data-cy="more-filters-button"
                data-tid="search_more_filter"
                elevation="0"
                variant="outlined"
                rounded
              >
                <span
                  class="bc-body c-black d-flex fw-500-important"
                  data-cy="more-filters"
                >
                  {{ $bct('More filters') }}
                </span>
              </v-btn>

              <v-dialog
                v-model="moreFiltersDialogIsVisible"
                tabindex="0"
                width="600px"
                scrollable
              >
                <v-card
                  border
                  flat
                  tile
                >
                  <v-card-title>
                    <v-row
                      class="d-flex align-center"
                      dense
                    >
                      <v-col class="d-flex justify-start">
                        {{ $bct('More filters') }}
                      </v-col>
                      <v-col class="d-flex justify-end">
                        <v-btn
                          @click="moreFiltersDialogIsVisible = false"
                          :aria-label="$bct('Close')"
                          icon="close"
                          variant="text"
                        />
                      </v-col>
                    </v-row>
                  </v-card-title>
                  <v-divider class="mb-4" />
                  <v-card-text class="pt-0 pb-6">
                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .program_types.filterable
                      "
                    >
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{
                            $bct(
                              store.state.schemas[subsidyProgram.provider_schema_id].definition
                                .properties.program_types.alias || 'Program types',
                            )
                          }}
                        </v-col>
                      </v-row>
                      <v-row
                        class="mb-4"
                        dense
                      >
                        <v-col>
                          <v-checkbox
                            v-for="type in store.state.schemas[subsidyProgram.provider_schema_id]
                              .definition.properties.program_types.items.enum"
                            v-model="filters.program_types"
                            :key="type"
                            :label="capitalize($bct(type))"
                            :value="type"
                            class="c-black mt-0"
                            color="primary"
                            hide-details
                            multiple
                          />
                        </v-col>
                      </v-row>
                      <v-divider class="my-6" />
                    </div>

                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .categories.filterable
                      "
                    >
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{
                            $bct(
                              store.state.schemas[subsidyProgram.provider_schema_id].definition
                                .properties.categories.alias || 'Categories',
                            )
                          }}
                        </v-col>
                      </v-row>

                      <v-row
                        class="mb-4"
                        dense
                      >
                        <v-col>
                          <div
                            v-if="
                              store.state.schemas[subsidyProgram.provider_schema_id].definition
                                .properties.categories.items?.enum?.length < 20
                            "
                          >
                            <v-checkbox
                              v-for="type in store.state.schemas[subsidyProgram.provider_schema_id]
                                .definition.properties.categories.items.enum"
                              v-model="filters.categories"
                              :key="type"
                              :label="capitalize($bct(type))"
                              :value="type"
                              class="c-black mt-0"
                              color="primary"
                              hide-details
                              multiple
                            />
                          </div>
                          <div v-else>
                            <v-autocomplete
                              v-model="filters.categories"
                              :items="
                                store.state.schemas[subsidyProgram.provider_schema_id].definition
                                  .properties.categories.items.enum
                              "
                              :label="$bct('Select all that apply')"
                              variant="filled"
                              chips
                              closable-chips
                              hide-details
                              multiple
                            />
                          </div>
                        </v-col>
                      </v-row>
                      <v-divider class="my-6" />
                    </div>

                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .program_season.filterable
                      "
                    >
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{
                            $bct(
                              store.state.schemas[subsidyProgram.provider_schema_id].definition
                                .properties.program_season.alias || 'Season',
                            )
                          }}
                        </v-col>
                      </v-row>
                      <v-row
                        class="mb-4"
                        dense
                      >
                        <v-col>
                          <v-radio-group
                            v-model="filters.program_season"
                            class="mt-0"
                            hide-details
                          >
                            <v-radio
                              v-for="type in store.state.schemas[subsidyProgram.provider_schema_id]
                                .definition.properties.program_season.enum"
                              :key="type"
                              :label="capitalize($bct(type))"
                              :value="type"
                              class="c-black mt-0"
                              color="primary"
                              hide-details
                            />
                          </v-radio-group>
                        </v-col>
                      </v-row>
                      <v-divider class="my-6" />
                    </div>

                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .quality_rating_score.filterable
                      "
                    >
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{ $bct('Quality rating') }}
                        </v-col>
                      </v-row>

                      <v-row dense>
                        <v-col>
                          <div v-if="store.state.site.ratings.Description">
                            <div class="c-light-black fs-15 pt-2 pb-4">
                              <span
                                v-text="store.state.site.ratings.Description"
                                class="c-light-black fs-15"
                              />
                              <a
                                v-if="store.state.site.ratings.Link"
                                :href="store.state.site.ratings.Link"
                                class="c-primary fs-15 ms-1 underlined"
                                target="_blank"
                              >
                                {{ $bct('Learn more') }}
                              </a>
                            </div>
                          </div>
                          <v-radio-group
                            v-model="filters.minimum_quality_rating"
                            class="mt-0 mb-2"
                            hide-details
                          >
                            <div
                              v-for="(level, index) in store.state.site.ratings.text
                                .slice()
                                .reverse()"
                              :key="index"
                            >
                              <RadioWithContext
                                :index="index"
                                :item="level"
                                :suffix="
                                  store.state.site.ratings.text.length - index <
                                  store.state.site.ratings.text.length
                                    ? 'or higher'
                                    : null
                                "
                                :value="store.state.site.ratings.max - index"
                              />
                              <v-divider
                                v-if="level.subtitle"
                                v-show="index + 1 < store.state.site.ratings.text.length"
                                class="my-3"
                              />
                            </div>
                          </v-radio-group>
                          <v-btn
                            v-show="filters.minimum_quality_rating"
                            @click="filters.minimum_quality_rating = null"
                            color="primary"
                            size="small"
                          >
                            {{ $bct('Clear filter') }}
                          </v-btn>
                        </v-col>
                      </v-row>
                      <v-divider class="my-6" />
                    </div>

                    <div
                      v-if="
                        store.state.schemas[subsidyProgram.provider_schema_id].definition.properties
                          .hours.filterable
                      "
                    >
                      <v-row dense>
                        <v-col class="c-black fs-16 fw-600">
                          {{ $bct('Hours') }}
                        </v-col>
                      </v-row>
                      <v-row>
                        <LabeledOpenClose
                          v-model="hoursRange"
                          close-title="Pickup time"
                          open-title="Drop-off time"
                          tabindex="0"
                          title-class="fs-14"
                        />
                      </v-row>

                      <div class="c-gray fs-15 mt-2 mb-3 mxw-400">
                        {{
                          $bct(
                            'We’ll show you results that offer drop-off at the time requested or earlier, and pick-up at the time requested or later',
                          )
                        }}
                      </div>

                      <v-row dense>
                        <v-col
                          class="fw-600 fs-14"
                          cols="12"
                        >
                          {{ $bct('Days of the week') }}
                        </v-col>
                        <v-col cols="12">
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Monday')"
                            class="mt-0"
                            density="compact"
                            value="0"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Tuesday')"
                            class="mt-2"
                            density="compact"
                            value="1"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Wednesday')"
                            class="mt-2"
                            density="compact"
                            value="2"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Thursday')"
                            class="mt-2"
                            density="compact"
                            value="3"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Friday')"
                            class="mt-2"
                            density="compact"
                            value="4"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Saturday')"
                            class="mt-2"
                            density="compact"
                            value="5"
                            hide-details
                          />
                          <v-checkbox
                            v-model="filters.week_days"
                            :label="$bct('Sunday')"
                            class="mt-2"
                            density="compact"
                            value="6"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                      <v-divider class="my-6" />
                    </div>

                    <SearchMoreFilters :value="filters" />
                  </v-card-text>

                  <v-divider />

                  <v-card-actions class="pt-2">
                    <v-btn
                      @click="clearMoreFilters()"
                      color="primary"
                      data-cy="clear-filters"
                      variant="text"
                    >
                      {{ $bct('Clear filters') }}
                    </v-btn>
                    <v-spacer />
                    <v-btn
                      @click="searchProviders()"
                      color="primary"
                      data-cy="apply-filter"
                    >
                      {{ $bct('Apply') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-col>

            <v-col
              class="ta-right"
              cols="12"
              sm="4"
            >
              <v-btn
                @click="listView = !listView"
                class="bg-white focus-very-visible"
                color="primary"
                data-cy="results-mode-button"
                variant="outlined"
              >
                <v-icon
                  v-show="!listView"
                  icon="west"
                  start
                />
                {{ $bct(!listView ? 'List view' : 'Map view') }}
                <v-icon
                  v-show="listView"
                  icon="east"
                  end
                />
              </v-btn>
            </v-col>
          </v-row>

          <v-row
            v-if="searchFlags.dobSearchEnabled"
            class="mb-3 mt-0"
          >
            <v-col>
              <span class="fs-16 mr-1">
                {{ $bct(`Search results show options beginning on`) }}
              </span>
              <StandardButton
                @click="openCareNeededDateDialog = !openCareNeededDateDialog"
                :custom-classes="['bg-white', 'rounded-pill']"
                :text="formattedCareNeededDate"
                color="primary"
                variant="outlined"
              />
            </v-col>

            <v-dialog
              v-model="openCareNeededDateDialog"
              max-width="300"
            >
              <DatePicker
                @update:model-value="changeCareNeededDate"
                :allowed-dates="allowedDates"
                :model-value="$vuetify.date.parseISO(careNeededDate)"
                scrollable
              >
                <template #actions>
                  <v-row>
                    <v-col
                      class="d-flex justify-start"
                      cols="6"
                    >
                      <v-btn
                        @click="resetCareNeededDate"
                        :loading="localProcessing"
                        :ripple="false"
                        class="focus-very-visible"
                        color="primary"
                        size="large"
                        variant="outlined"
                      >
                        {{ $bct('Reset') }}
                      </v-btn>
                    </v-col>
                    <v-col
                      class="d-flex justify-end"
                      cols="6"
                    >
                      <v-btn
                        @click="applyCareNeededDate()"
                        :loading="localProcessing"
                        :ripple="false"
                        class="focus-very-visible"
                        color="primary"
                        size="large"
                      >
                        {{ $bct('Apply') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </template>
              </DatePicker>
            </v-dialog>
          </v-row>
        </template>

        <v-row
          v-if="searched && searchTab == 1"
          class="d-flex align-center mb-1 mt-2"
        >
          <v-col
            class="ta-right"
            cols="12"
            sm="12"
          >
            <v-btn
              @click="listView = !listView"
              class="bg-white focus-very-visible"
              color="primary"
              data-cy="results-mode-button"
              variant="outlined"
            >
              <v-icon
                v-show="!listView"
                icon="west"
                start
              />
              {{ $bct(!listView ? 'List view' : 'Map view') }}
              <v-icon
                v-show="listView"
                icon="east"
                end
              />
            </v-btn>
          </v-col>
        </v-row>

        <div v-show="!listView">
          <ProviderPreferenceMapResults
            @select="selectedMatch = $event"
            ref="map"
            :matches="matches"
            :search="search"
            :search-counter="searchCounter"
            :visible="!listView"
          />

          <template v-if="selectedMatch">
            <ProviderProgramsCard
              @check="$emit('check', $event)"
              @open="$emit('open', selectedMatch.id)"
              @toggle="$emit('toggle', $event)"
              :key="selectedMatch.id"
              :dob="child.dob"
              :program-ids="programIds"
              :provider="selectedMatch"
              :selectable="belowSelectionLimit"
              :subsidy-program="subsidyProgram"
            />
          </template>
        </div>

        <div v-show="listView">
          <ProviderProgramsCard
            v-for="provider in matches"
            @check="$emit('check', $event)"
            @open="$emit('open', provider.id)"
            @toggle="$emit('toggle', $event)"
            :key="provider.id"
            :dob="child.dob"
            :program-ids="programIds"
            :provider="provider"
            :selectable="belowSelectionLimit"
            :subsidy-program="subsidyProgram"
            class="my-4"
          />
        </div>

        <template v-if="matches.length > 0 && searchTab == 0">
          <v-card
            class="bottom-0 elevation-0 bg-super-light-blue w-100pc pb-2 pt-1"
            tile
          >
            <v-pagination
              @update:model-value="changePage($event)"
              :disabled="localProcessing"
              :length="pages"
              :model-value="page"
              :total-visible="8"
              rounded
            />
          </v-card>
        </template>
        <v-card
          v-show="searched && !searching && matches.length === 0"
          class="bottom-0 elevation-0 bg-super-light-blue w-100pc pb-2 pt-1"
          tile
        >
          <v-alert
            border="start"
            class="w-100pc mt-6 fs-20"
            color="secondary"
            type="info"
          >
            <div>
              <MarkdownContent :content="noProviderResultsMessage()" />
            </div>
          </v-alert>
        </v-card>
      </div>

      <vertical-spacer :min-height="50" />
    </div>

    <v-divider />

    <v-row class="my-4 px-4">
      <v-col class="d-flex justify-end">
        <v-btn
          @click="proceedWithSelections"
          :disabled="programIds.length < (subsidyProgram.selection_minimum || 1)"
          append-icon="east"
          class="focus-very-visible"
          color="primary"
          data-testid="selectionsContinueButton"
          size="x-large"
        >
          {{ $bct('Continue with selections') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, toRaw } from 'vue';
import { useDate, useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import { calculateMonthsOldOnDate } from '@/parent/services/calculate-months-old-on-date';
import { capitalize } from '@/plugins/filters';
import childrenSearchParam from '@/parent/services/children-search-param';
import { dateObjectToIso8601 } from '@/shared/services/date-helper';
import DatePicker from '@/shared/components/DatePicker.vue';
import FilterMenu from '@/shared/components/form/FilterMenu.vue';
import LabeledOpenClose from '@/shared/components/form/LabeledOpenClose.vue';
import MarkdownContent from '@/shared/components/MarkdownContent.vue';
import propsToRefs from '@/shared/utils/propsToRefs';
import ProviderPreferenceMapResults from '@/parent/components/subsidy/ProviderPreferenceMapResults.vue';
import ProviderProgramsCard from '@/parent/components/subsidy/ProviderProgramsCard.vue';
import RadioWithContext from '@/shared/components/form/RadioWithContext.vue';
import SearchFlags from '@/parent/services/search-flags';
import SearchMoreFilters from '@/shared/components/search/MoreFilters.vue';
import SelectedProgramCard from '@/parent/components/subsidy/SelectedProgramCard.vue';
import useApi from '@/shared/composables/useApi';
import useDateFormatting from '@/shared/composables/useDateFormatting';
import useEventBus from '@/shared/composables/useEventBus';
import VerticalSpacer from '@/shared/components/VerticalSpacer.vue';

const { allowedDates } = useDateFormatting();
const { api: Api } = useApi();
const eventBus = useEventBus();
const { locale } = useI18n();
const { smAndDown } = useDisplay();
const store = useStore();
const vuetifyDate = useDate();

const $bct = inject('translator');

const emit = defineEmits(['check', 'copy', 'move', 'next', 'open', 'remove', 'toggle']);

const props = defineProps({
  child: {
    type: Object,
    default: null,
  },
  defaultQuery: {
    type: String,
    default: null,
  },
  previousChildName: {
    type: String,
    default: null,
  },
  previousSubsidyProgramIds: {
    type: Array,
    default: null,
  },
  processing: Boolean,
  programIds: {
    type: Array,
    default: null,
  },
  selectedProviders: {
    type: Array,
    default: null,
  },
  showSelected: Boolean,
  subsidy: {
    type: Object,
    default: null,
  },
  subsidyProgram: {
    type: Object,
    default: null,
  },
});

const [localProcessingFromProps] = propsToRefs(props, ['processing']);
const searchRequestPending = ref(false);
const localProcessing = computed(
  () => localProcessingFromProps.value || searchRequestPending.value,
);

const searchFlags = new SearchFlags(store);

function createDefaultSearch() {
  return {
    categories: [],
    custom: {},
    dropoff: null,
    eligibility_dob: null,
    highlights: {},
    minimum_quality_rating: null,
    payment_subsidies_accepted: [],
    pickup: null,
    program_season: null,
    program_types: [],
    schema_id: props.subsidyProgram.provider_schema_id,
    vacancy: null,
    week_days: [],
    show_financial_aid_options: null,
    payment_other_programs: [],
  };
}

const careNeededDate = ref(
  props.subsidyProgram.search_for_care_as_of_date || new Date().toLocaleDateString('en-CA'),
);
const careNeededDateChanged = ref(false);
const excludeIneligibleByDobChecked = ref(false);
const filters = reactive(createDefaultSearch());
const hoursRange = ref([900, 1600]);
const listView = ref(true);
const matches = ref([]);
const moreFiltersDialogIsVisible = ref(null);
const openCareNeededDateDialog = ref(false);
const page = ref(1);
const pages = ref(1);
const search = ref(null);
const searchCounter = ref(0);
const searched = ref(false);
const searching = ref(false);
const searchQuery = ref(props.defaultQuery);
const searchQueryChanged = ref(!!props.defaultQuery);
const searchTab = ref(0);
const previousSearchTab = ref(0);
const selectedMatch = ref(null);
const viewSelectedResults = ref(props.showSelected);

const addressSnapshot = ref(null);
const nameSnapshot = ref(createEmptyNameSnapshot());

const belowSelectionLimit = computed(() => {
  if (props.subsidyProgram.selection_limit) {
    return props.programIds.length < props.subsidyProgram.selection_limit;
  }
  return true;
});

const formattedCareNeededDate = computed(() => {
  return new Date(careNeededDate.value).toLocaleDateString(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
});

const moreFiltersActive = computed(() => {
  return (
    filters.categories.length > 0 ||
    Object.values(filters.highlights).some((highlight) => highlight.length > 0) ||
    Object.values(filters.custom).some((item) => item && item.length > 0) ||
    filters.minimum_quality_rating ||
    filters.payment_subsidies_accepted.length > 0 ||
    filters.program_season ||
    filters.program_types.length > 0 ||
    !!filters.subsidy
  );
});

const moreFiltersButtonClass = computed(() => {
  let classes = ['me-2', 'bc-outlined-gray'];

  if (moreFiltersActive.value) {
    classes = classes.concat(['bg-white', 'bc-primary', 'b-1']);
  } else {
    classes = classes.concat(['bg-white', 'bc-very-light-gray']);
  }

  return classes.join(' ');
});

const checkboxFilterButtonClass = computed(() => {
  if (smAndDown) {
    return 'checkbox-filter-button ' + moreFiltersButtonClass.value;
  }
  return moreFiltersButtonClass.value;
});

function createEmptyNameSnapshot() {
  return {
    searchQuery: null,
    matches: [],
    searched: false,
    page: 1,
    pages: 1,
    searchQueryChanged: false,
    listView: true,
    selectedMatch: null,
    searchCounter: 0,
  };
}

function captureAddressState() {
  return {
    filters: structuredClone(toRaw(filters)),
    search: search.value ? { ...search.value } : null,
    matches: [...matches.value],
    searched: searched.value,
    searchQuery: searchQuery.value,
    page: page.value,
    pages: pages.value,
    careNeededDate: careNeededDate.value,
    hoursRange: [...hoursRange.value],
    searchQueryChanged: searchQueryChanged.value,
    listView: listView.value,
    selectedMatch: selectedMatch.value,
    careNeededDateChanged: careNeededDateChanged.value,
    searchCounter: searchCounter.value,
    excludeIneligibleByDobChecked: excludeIneligibleByDobChecked.value,
  };
}

function captureNameState() {
  return {
    searchQuery: searchQuery.value,
    matches: [...matches.value],
    searched: searched.value,
    page: page.value,
    pages: pages.value,
    searchQueryChanged: searchQueryChanged.value,
    listView: listView.value,
    selectedMatch: selectedMatch.value,
    searchCounter: searchCounter.value,
  };
}

function assignFiltersFromSnapshot(snapshotFilters) {
  const data = structuredClone(toRaw(snapshotFilters));
  Object.assign(filters, data);
}

function resetFiltersToDefault() {
  const defaults = createDefaultSearch();
  for (const key of Object.keys(filters)) {
    if (!(key in defaults)) {
      delete filters[key];
    }
  }
  Object.assign(filters, defaults);
}

function applyAddressState(snapshot) {
  if (!snapshot) return;
  assignFiltersFromSnapshot(snapshot.filters);
  search.value = snapshot.search ? { ...snapshot.search } : null;
  matches.value = [...snapshot.matches];
  searched.value = snapshot.searched;
  searchQuery.value = snapshot.searchQuery;
  page.value = snapshot.page;
  pages.value = snapshot.pages;
  careNeededDate.value = snapshot.careNeededDate;
  hoursRange.value = [...snapshot.hoursRange];
  searchQueryChanged.value = snapshot.searchQueryChanged;
  listView.value = snapshot.listView;
  selectedMatch.value = snapshot.selectedMatch;
  careNeededDateChanged.value = snapshot.careNeededDateChanged;
  searchCounter.value = snapshot.searchCounter;
  excludeIneligibleByDobChecked.value = snapshot.excludeIneligibleByDobChecked;
}

function applyNameState(snapshot) {
  const data = snapshot || createEmptyNameSnapshot();
  search.value = null;
  resetFiltersToDefault();
  matches.value = [...data.matches];
  searched.value = data.searched;
  searchQuery.value = data.searchQuery;
  page.value = data.page;
  pages.value = data.pages;
  searchQueryChanged.value = data.searchQueryChanged;
  listView.value = data.listView;
  selectedMatch.value = data.selectedMatch;
  searchCounter.value = data.searchCounter;
}

function onSearchTabChange(newTab) {
  const outgoing = previousSearchTab.value;
  if (outgoing === 0) {
    addressSnapshot.value = captureAddressState();
  } else {
    nameSnapshot.value = captureNameState();
  }
  if (newTab === 0) {
    applyAddressState(addressSnapshot.value);
  } else {
    applyNameState(nameSnapshot.value);
  }
  previousSearchTab.value = newTab;
  moreFiltersDialogIsVisible.value = false;
  searching.value = false;
  searchRequestPending.value = false;
}

onMounted(() => {
  addressSnapshot.value = captureAddressState();
});

async function applyCareNeededDate() {
  if (careNeededDateChanged.value) {
    await save();
  }
  openCareNeededDateDialog.value = false;
}

function changeCareNeededDate(value) {
  careNeededDate.value = vuetifyDate.toISO(value);
  searchQueryChanged.value = true;
  careNeededDateChanged.value = true;
}

async function changePage(newPage) {
  page.value = newPage;
  if (searchTab.value === 0) {
    searchRequestPending.value = true;
    try {
      await loadMatches();
    } catch (err) {
      eventBus.error(err);
      searching.value = false;
    } finally {
      searchRequestPending.value = false;
    }
  }
}

function clearHours() {
  hoursRange.value = [900, 1600];
  filters.dropoff = null;
  filters.pickup = null;
  filters.week_days = [];
}

async function clearMoreFilters() {
  clearHours();
  filters.show_financial_aid_options = null;
  filters.payment_other_programs = [];
  filters.categories = [];
  filters.custom = {};
  filters.highlights = {};
  filters.minimum_quality_rating = null;
  filters.payment_subsidies_accepted = [];
  filters.program_season = null;
  filters.program_types = [];
  moreFiltersDialogIsVisible.value = false;
  await searchProviders();
}

function copyPrevious() {
  viewSelectedResults.value = true;
  emit('copy');
}

function dobIneligible(program) {
  return (
    (props.child.dob && props.child.dob < program?.dob_start_date) ||
    props.child.dob > program?.dob_end_date
  );
}

function filterMatches(providers) {
  const monthsOld = calculateMonthsOldOnDate(props.child.dob, careNeededDate.value);

  return providers
    .map((provider) => ({
      ...provider,
      programs: (provider.programs ?? []).filter((program) => {
        const withinAgeRange = program.age_max >= monthsOld && monthsOld >= program.age_min;

        return filters.require_seat_availability
          ? withinAgeRange && program.meta?.seat_availability_flag !== 'none'
          : withinAgeRange;
      }),
    }))
    .filter((provider) => provider.programs.length > 0);
}

function getIneligibleProgramsText(programs) {
  const programNames = programs.map((program) => program.name + ' at ' + program.provider.name);

  let ineligibleProgramsText;

  if (programNames.length === 2) {
    ineligibleProgramsText = programNames.join(' or ');
  } else if (programNames.length > 2) {
    ineligibleProgramsText = `${programNames.slice(0, -1).join(', ')}, or ${programNames.slice(-1)[0]}`;
  } else {
    ineligibleProgramsText = programNames[0];
  }

  const plurality = programNames.length === 1 ? 'this program' : 'these programs';

  return `Your child does not meet requirements for ${ineligibleProgramsText}. Please remove ${plurality} before proceeding`;
}

async function getSelectedPrograms() {
  let programs = [];

  await Promise.all(
    props.programIds.map(async (programId) => {
      const resp = await Api.publicApi.organization.program.get(programId, {
        include: 'provider_program_requirements',
        subsidy_id: props.subsidy.id,
      });

      programs.push(resp.data);
    }),
  );

  return programs;
}

function hasIneligibleProviderProgramRequirements(program) {
  return (
    program?.provider_program_requirements_id &&
    program?.provider_program_requirements?.some((ppr) => !ppr.passes)
  );
}

async function loadMatches() {
  if (!search.value?.id) {
    return;
  }
  matches.value = [];
  const response = await Api.match.index(search.value.id, {
    page: page.value,
    page_size: 10,
    include: 'details,programs',
    subsidy_program_id: props.subsidyProgram.id,
  });

  if (response) {
    searching.value = false;
    searchCounter.value += 1;
    matches.value = filterMatches(response.data);
    pages.value = parseInt(response.headers['x-page-count'] || '0', 10);
  }
}

function noProviderResultsMessage() {
  if (props.subsidyProgram.no_provider_results_found_message) {
    return props.subsidyProgram.no_provider_results_found_message;
  }
  return 'No providers were found that matched your search criteria. Please try adjusting your search to see additional results.';
}

async function proceedWithSelections() {
  const programs = await getSelectedPrograms();

  const selectedIneligiblePrograms = programs.filter((program) => programIsIneligible(program));

  if (selectedIneligiblePrograms.length > 0) {
    eventBus.error(getIneligibleProgramsText(selectedIneligiblePrograms));
  } else {
    emit('next');
  }
}

function programIsIneligible(program) {
  return dobIneligible(program) || hasIneligibleProviderProgramRequirements(program);
}

function resetCareNeededDate() {
  if (careNeededDate.value === props.subsidyProgram.search_for_care_as_of_date) return;

  careNeededDate.value =
    props.subsidyProgram.search_for_care_as_of_date || dateObjectToIso8601(new Date());
  searchQueryChanged.value = true;
}

async function save() {
  await searchProviders();
}

async function searchByAddress() {
  if (filters.week_days.length > 0) {
    filters.dropoff = hoursRange.value[0];
    filters.pickup = hoursRange.value[1];
  } else {
    filters.dropoff = null;
    filters.pickup = null;
  }

  const children = childrenSearchParam({
    searchFlags,
    child: props.child,
    careNeededDate: careNeededDate.value,
    toArray: true,
  });

  searched.value = true;
  const response = await Api.search.create({
    ...filters,
    children,
    categories: filters.categories,
    unformatted_origin: searchQuery.value,
    location_type: 'home',
    subsidy_program_id: props.subsidyProgram.id,
    subsidy_id: props.subsidy.id,
  });

  if (response) {
    search.value = response.data;
    page.value = 1;
    searchQueryChanged.value = false;
    await loadMatches();
  }
}

async function searchByQuery() {
  searchCounter.value += 1;
  const counter = searchCounter.value;
  searched.value = true;
  matches.value = [];
  const response = await Api.publicApi.provider.index({
    include: 'details,programs',
    query: searchQuery.value,
    schema_id: props.subsidyProgram.provider_schema_id,
    subsidy_program_id: props.subsidyProgram.id,
    subsidy_id: props.subsidy.id,
  });

  if (response) {
    if (counter === searchCounter.value) {
      matches.value = filterMatches(response.data);
      searching.value = false;
      searchQueryChanged.value = false;
      pages.value = 0;
    }
  }
}

async function searchProviders() {
  searching.value = true;
  moreFiltersDialogIsVisible.value = false;

  if (!searchQuery.value) {
    matches.value = [];
    searching.value = false;
    return;
  }

  searchRequestPending.value = true;
  try {
    if (searchTab.value === 0) {
      await searchByAddress();
    } else {
      await searchByQuery();
    }
  } catch (err) {
    eventBus.error(err);
    searching.value = false;
  } finally {
    searchRequestPending.value = false;
  }
}

async function toggleSearchDobEligibilityFilter() {
  filters.eligibility_dob = excludeIneligibleByDobChecked.value ? props.child.dob : null;
  await save();
}
</script>

<style>
.checkbox-filter-button {
  height: auto !important;
  margin-bottom: 8px;
}

.v-input__details {
  display: none !important;
}
</style>
